<template>
  <div class="container mx-auto px-4 py-8">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Compliances</h1>
    </div>

    <!-- Active Compliances -->
    <div class="mb-8">
      <h2 class="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Active Compliances</h2>
      <div v-if="store.state.loading" class="text-center py-4">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
        <p class="mt-2 text-gray-600 dark:text-gray-400">Loading compliances...</p>
      </div>
      <div v-else-if="!filteredActiveCompliances.length" class="text-center py-4 text-gray-600 dark:text-gray-400">
        No active compliances found.
      </div>
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="compliance in filteredActiveCompliances"
          :key="compliance.id"
          class="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1 min-w-0">
              <div class="flex items-center space-x-2 mb-2">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ compliance.name }}</h3>
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 flex-shrink-0">
                  <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Active
                </span>
              </div>
              <p class="text-xs text-gray-600 dark:text-gray-300 mb-3 line-clamp-2 leading-relaxed">{{ compliance.description }}</p>
              <div class="flex items-center space-x-2">
                <span class="text-xs text-gray-500 dark:text-gray-400">
                  Added {{ formatDate(compliance.created_at) }}
                </span>
              </div>
            </div>
            <div class="flex space-x-2 flex-shrink-0">
              <button
                @click.prevent="disableCompliance(compliance)"
                class="p-1 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                title="Disable Compliance"
                type="button"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Available Compliances -->
    <div>
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Available Compliances</h2>
        <div class="relative w-64">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search compliances..."
            class="input"
          />
          <div class="absolute inset-y-0 right-0 flex items-center pr-3">
            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>
      <div v-if="store.state.loading" class="text-center py-4">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
        <p class="mt-2 text-gray-600 dark:text-gray-400">Loading compliances...</p>
      </div>
      <div v-else-if="!filteredAvailableCompliances.length" class="text-center py-4 text-gray-600 dark:text-gray-400">
        No available compliances found.
      </div>
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="compliance in filteredAvailableCompliances"
          :key="compliance.id"
          class="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer"
          @click="enableCompliance(compliance)"
        >
          <div class="flex items-start">
            <div class="flex-1 min-w-0">
              <div class="flex items-center space-x-2 mb-2">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ compliance.name }}</h3>
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 flex-shrink-0">
                  <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Available
                </span>
              </div>
              <p class="text-xs text-gray-600 dark:text-gray-300 line-clamp-2 leading-relaxed">{{ compliance.description }}</p>
            </div>
            <div class="ml-4 flex-shrink-0">
              <button
                class="p-2 text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                title="Enable Compliance"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <DeleteConfirmationModal
      :show="showDeleteModal"
      title="Confirm Disable Compliance"
      :message="disableMessage"
      confirm-text="Disable"
      :loading="isDisabling"
      @confirm="handleDisableConfirm"
      @cancel="showDeleteModal = false"
    />
  </div>
</template>

<script>
import { computed, onMounted, watch, ref } from 'vue'
import { useStore } from 'vuex'
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue'

export default {
  name: 'CompliancesView',
  components: {
    DeleteConfirmationModal
  },
  setup() {
    const store = useStore()
    const searchQuery = ref('')
    const showDeleteModal = ref(false)
    const complianceToDisable = ref(null)
    const isDisabling = ref(false)

    const fetchCompliances = async () => {
      const currentTenantId = store.state.currentTenantId
      if (!currentTenantId) return

      try {
        store.commit('setLoading', true)
        store.commit('setError', null)

        const allCompliancesResponse = await store.dispatch('fetchAllCompliances')
        if (Array.isArray(allCompliancesResponse)) {
          store.commit('setAllCompliances', allCompliancesResponse)
        } else if (allCompliancesResponse?.data && Array.isArray(allCompliancesResponse.data)) {
          store.commit('setAllCompliances', allCompliancesResponse.data)
        } else {
          store.commit('setError', 'Invalid response format for compliances')
          store.commit('setAllCompliances', [])
        }

        await store.dispatch('compliances/fetchData', {
          tenantId: currentTenantId,
          page: 1,
          entriesPerPage: 100,
          search: '',
          orderBy: 'compliance_id',
          sortDirection: 'asc'
        })
      } catch (error) {
        store.commit('setError', error.response?.data?.message || 'Failed to fetch compliances')
      } finally {
        store.commit('setLoading', false)
      }
    }

    const activeCompliances = computed(() => {
      const compliancesData = store.state.compliances.data
      return Array.isArray(compliancesData) ? compliancesData : []
    })

    const availableCompliances = computed(() => {
      const allCompliances = store.state.allCompliances
      if (!Array.isArray(allCompliances)) return []

      const activeIds = new Set(store.state.compliances.data?.map(c => c.id).filter(Boolean) || [])
      return allCompliances.filter(c => c && !activeIds.has(c.id))
    })

    const filteredActiveCompliances = computed(() => 
      activeCompliances.value.filter(compliance => 
        compliance && 
        typeof compliance === 'object' && 
        compliance.id && 
        compliance.name && 
        compliance.description
      )
    )

    const filteredAvailableCompliances = computed(() => {
      const searchLower = searchQuery.value.toLowerCase()
      return availableCompliances.value
        .filter(compliance => 
          compliance && 
          typeof compliance === 'object' && 
          compliance.id && 
          compliance.name && 
          compliance.description
        )
        .filter(compliance => 
          !searchQuery.value || 
          compliance.name.toLowerCase().includes(searchLower) ||
          compliance.description.toLowerCase().includes(searchLower)
        )
    })

    const disableMessage = computed(() => {
      if (!complianceToDisable.value) {
        return 'Are you sure you want to disable this compliance? This action cannot be undone.'
      }
      return `Are you sure you want to disable "${complianceToDisable.value.name}"? This action cannot be undone.`
    })

    const enableCompliance = async (compliance) => {
      try {
        const currentTenantId = store.state.currentTenantId
        if (!currentTenantId) {
          throw new Error('No tenant selected')
        }

        const complianceMapping = {
          tenant_id: currentTenantId,
          compliance_id: compliance.id,
          public_id: `comp-${currentTenantId}-${compliance.id}-${Date.now()}`
        }

        await store.dispatch('compliances/createData', {
          tenantId: currentTenantId,
          data: complianceMapping
        })

        await fetchCompliances()
      } catch (error) {
        store.commit('setError', error.response?.data?.message || 'Failed to enable compliance')
      }
    }

    const disableCompliance = (compliance) => {
      if (!compliance) return

      const complianceData = compliance._rawValue || compliance
      if (!complianceData?.id) return

      complianceToDisable.value = complianceData
      showDeleteModal.value = true
    }

    const handleDisableConfirm = async () => {
      if (!complianceToDisable.value) return

      try {
        isDisabling.value = true
        const currentTenantId = store.state.currentTenantId
        if (!currentTenantId) {
          throw new Error('No tenant selected')
        }

        await store.dispatch('compliances/deleteData', {
          tenantId: currentTenantId,
          id: complianceToDisable.value.id
        })

        await fetchCompliances()
        showDeleteModal.value = false
        complianceToDisable.value = null
      } catch (error) {
        store.commit('setError', error.response?.data?.message || 'Failed to disable compliance')
        await fetchCompliances()
      } finally {
        isDisabling.value = false
      }
    }

    const formatDate = (date) => {
      if (!date) return ''
      try {
        return new Date(date).toLocaleDateString()
      } catch (error) {
        console.error('Error formatting date:', error)
        return ''
      }
    }

    // Watch for tenant changes
    watch(() => store.state.currentTenantId, (newTenantId) => {
      if (newTenantId) {
        fetchCompliances()
      } else {
        // Clear compliances when no tenant is selected
        store.commit('setCompliances', [])
        store.commit('setAllCompliances', [])
      }
    })

    onMounted(() => {
      if (store.state.currentTenantId) {
        fetchCompliances()
      }
    })

    return {
      store,
      searchQuery,
      activeCompliances,
      availableCompliances,
      filteredActiveCompliances,
      filteredAvailableCompliances,
      formatDate,
      enableCompliance,
      disableCompliance,
      showDeleteModal,
      complianceToDisable,
      isDisabling,
      disableMessage,
      handleDisableConfirm
    }
  }
}
</script>

<style scoped>
.container {
  @apply max-w-7xl mx-auto;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.15s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>